import { yupResolver } from '@hookform/resolvers/yup';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { IntlFormatters, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useRegistrationMutation } from '../../apiRtk/legacyFeApi';
import { useHasFeature } from '../../components/Feature/Feature';
import featureFlags from '../../config/featureFlags';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError, addSnackbarSuccess } from '../../modules/notifications/actions';
import messages from './messages';
import { getRegisterDefaultValues, getRegisterValidationSchema, IRegisterFormData } from './validationSchema';

export interface IUseRegister {
  formatMessage: IntlFormatters['formatMessage'];
  register: (values: IRegisterFormData) => Promise<void>;
  isFetching: boolean;
  isRegistrationFinished: boolean;
  formData: UseFormReturn<IRegisterFormData, any, undefined>;
  isOryRegistrationFFEnabled: boolean;
}

export function useRegister(): IUseRegister {
  const [register, { isLoading, status }] = useRegistrationMutation();
  const isOryRegistrationFFEnabled = useHasFeature(featureFlags.ORY_REGISTRATION);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'recaptcha';
    script.src = 'https://www.google.com/recaptcha/api.js';
    script.async = true;
    script.defer = true;

    document.body.appendChild(script);
    return () => {
      document.getElementById('recaptcha')?.remove();
    };
  });

  const formData = useForm<IRegisterFormData>({
    defaultValues: getRegisterDefaultValues(isOryRegistrationFFEnabled),
    mode: 'onChange',
    resolver: yupResolver<IRegisterFormData>(
      getRegisterValidationSchema({ formatMessage, isOryRegistrationFFEnabled })
    ),
  });

  const registerUserWithCompany = async (values: IRegisterFormData) => {
    try {
      await register({ body: { ...values, action: 'NEW_REGISTRATION', newsletters: !values.newsletters } }).unwrap();
      dispatch(addSnackbarSuccess(messages.authSnackbarConfirmationEmail));
    } catch (error) {
      if ((error as { status: number }).status === 409) {
        dispatch(addSnackbarError({ ...messages.registerEmailUsed }, TYPE_DESCRIPTOR));
        formData.setError('email', {
          message: formatMessage(messages.registerEmailUsed),
        });
      } else {
        dispatch(addSnackbarError({ ...messages.SharedNotificationErrTryAgain }, TYPE_DESCRIPTOR));
      }
    }
  };

  return {
    formatMessage,
    formData,
    isFetching: isLoading,
    isOryRegistrationFFEnabled,
    isRegistrationFinished: status === QueryStatus.fulfilled,
    register: registerUserWithCompany,
  };
}
